// A tiny framework-independent observable. React subscribes to the call
// engine's published state through this (via `useSyncExternalStore`) without the
// engine ever importing React. Snapshots are immutable objects replaced whole,
// so referential-equality checks in the UI are cheap and correct.

export type Listener = () => void;
export type Unsubscribe = () => void;

export interface ReadableStore<T> {
  /** Current immutable snapshot. */
  get(): T;
  /** Subscribe to changes; returns an unsubscribe function. */
  subscribe(listener: Listener): Unsubscribe;
}

export class Store<T> implements ReadableStore<T> {
  private value: T;
  private readonly listeners = new Set<Listener>();

  constructor(initial: T) {
    this.value = initial;
  }

  get(): T {
    return this.value;
  }

  /** Replace the snapshot with `next` and notify subscribers if it changed. */
  set(next: T): void {
    if (Object.is(next, this.value)) return;
    this.value = next;
    this.emit();
  }

  /** Shallow-merge a patch into the current snapshot (object states only). */
  patch(patch: Partial<T>): void {
    this.set({ ...this.value, ...patch });
  }

  subscribe(listener: Listener): Unsubscribe {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private emit(): void {
    for (const l of [...this.listeners]) l();
  }
}
